import { useEffect } from 'react';
import { assetsUrl } from '../util/constants';
import { getRandomArrayElement } from '../util/getRandomArrayElement';
import { useNavigate } from 'react-router-dom';
import {
   exerciseListQueryOptions,
   exerciseProgressSummaryOptions,
   getAllExerciseProgressSummary,
} from '../exercises/exerciseData';
import { useQueries } from '@tanstack/react-query';
import {
   courseDocumentQueryOptions,
   courseProgressQueryOptions,
} from '../courses/queryGroups';
import { useProcessedLessonProgress } from '../courses/useProcessedLessonProgress';
import { ShowSiteLoading } from '../SiteLoadingIndicator';

const idArrayToPracticeUrl = (array, unplayedId, sessionsForUnplayed) => {
   const count = unplayedId === array[0] ? sessionsForUnplayed : '1';

   if (array.length === 1) {
      return `/app/exercise/${array[0]}/?session-count=${count}&next=/app/daily-complete`;
   }

   return (
      `/app/exercise/${array[0]}/?session-count=${count}&next=` +
      encodeURIComponent(
         idArrayToPracticeUrl(array.slice(1), unplayedId, sessionsForUnplayed)
      )
   );
};

const DailyPractice = () => {
   const courseId = 'mixable';

   const [
      { data: courseDocument },
      { data: courseProgress },
      { data: exerciseList },
      { data: exerciseProgressSummaries },
   ] = useQueries({
      queries: [
         courseDocumentQueryOptions(courseId),
         courseProgressQueryOptions(courseId),
         exerciseListQueryOptions(),
         exerciseProgressSummaryOptions(),
      ],
   });

   if (
      courseDocument == null ||
      courseProgress == null ||
      exerciseList == null ||
      exerciseProgressSummaries == null
   ) {
      return <ShowSiteLoading />;
   }

   return (
      <DailyBuilder
         courseDocument={courseDocument}
         courseProgress={courseProgress}
         exerciseList={exerciseList}
         exerciseProgressSummaries={exerciseProgressSummaries}
      />
   );
};

const DailyBuilder = ({
   courseDocument,
   courseProgress,
   exerciseList,
   exerciseProgressSummaries,
}) => {
   const navigate = useNavigate();

   const { completedLessonsSet } = useProcessedLessonProgress(
      courseDocument,
      courseProgress
   );

   useEffect(() => {
      let courseLocked = {};
      let courseUnlocked = {};

      if (courseDocument.dailyChallengeUnlocks != null) {
         courseDocument.dailyChallengeUnlocks.forEach(
            (id) => (courseUnlocked[id] = true)
         );
      }
      courseDocument.sections.forEach((section) => {
         let sectionUnlocked = false;

         section.lessons.forEach((lesson) => {
            let lessonUnlocked = false;
            if (completedLessonsSet[lesson.id]) {
               sectionUnlocked = true;
               lessonUnlocked = true;
            }

            if (lesson.dailyChallengeUnlocks != null) {
               lesson.dailyChallengeUnlocks.forEach((id) => {
                  if (lessonUnlocked) {
                     courseUnlocked[id] = true;
                  } else {
                     courseLocked[id] = true;
                  }
               });
            }
         });

         if (section.dailyChallengeUnlocks != null) {
            section.dailyChallengeUnlocks.forEach((id) => {
               if (sectionUnlocked) {
                  courseUnlocked[id] = true;
               } else {
                  courseLocked[id] = true;
               }
            });
         }
      });

      const allIds = exerciseList
         .filter(({ id }) => courseUnlocked[id])
         .reduce(
            (idArray, exercise) => [
               ...idArray,
               exercise.id,
               ...exercise.modulesList
                  // remove modules that are not unlocked for exercise
                  .filter(
                     ({ unlockAfter }) =>
                        exerciseProgressSummaries[exercise.id]?.currentLevel >
                        unlockAfter
                  )
                  // remove modules that are not unlocked for course
                  .filter(({ id }) => !courseLocked[`${exercise.id}/${id}`])
                  .map(({ id }) => `${exercise.id}/${id}`),
            ],
            []
         );

      const selectedForPractice = [];

      let nextIds = allIds;
      let unplayedExerciseId;

      while (selectedForPractice.length < 5) {
         const nextSelected = getRandomArrayElement(nextIds);

         if (nextSelected == null) {
            break;
         }

         const [exerciseId, moduleId] = nextSelected.split('/');

         const progressData =
            moduleId == null
               ? exerciseProgressSummaries[exerciseId]
               : exerciseProgressSummaries[exerciseId]?.modules[moduleId];

         if (unplayedExerciseId == null || progressData != null) {
            selectedForPractice.push(nextSelected);
         }

         if (unplayedExerciseId == null && progressData == null) {
            unplayedExerciseId = nextSelected;
         }

         nextIds = nextIds.filter((id) => nextSelected != id);
      }

      let url = idArrayToPracticeUrl(
         selectedForPractice,
         unplayedExerciseId,
         Math.min(6 - selectedForPractice.length, 3)
      );

      navigate(url, {
         replace: true,
      });
   }, [
      courseDocument,
      completedLessonsSet,
      exerciseList,
      exerciseProgressSummaries,
   ]);

   return null;
};

export { DailyPractice };
